import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  Chip,
  Stack,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import { useConfirm } from '../../hooks/useConfirm';
import { cashReceiptsService } from '../../services/cashReceipts.service';
import config from '../../config';
import axios from 'axios';
import CashReceiptDialog from '../../components/vouchers/CashReceiptDialog';
import VoucherPreviewDialog from '../../components/vouchers/VoucherPreviewDialog';
import { VoucherDto, CreateVoucherDto, UpdateVoucherDto } from '../../types/vouchers.types';
import { VOUCHER_STATUS } from '../../constants/vouchers.constants';

const CashReceiptsPage: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const confirm = useConfirm();

  const userRoles = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return [] as string[];
      const payload = JSON.parse(atob(token.split('.')[1]));
      const roles = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      return roles ? (Array.isArray(roles) ? roles : [roles]) : [];
    } catch { return [] as string[]; }
  })();
  const canPost = (() => { try { const token = localStorage.getItem('token'); if (!token) return false; const payload = JSON.parse(atob(token.split('.')[1])); const perms = payload.Permission ? (Array.isArray(payload.Permission) ? payload.Permission : [payload.Permission]) : []; return perms.some((p: string) => p.endsWith('_POST')); } catch { return false; } })();

  // State
  const [vouchers, setVouchers] = useState<VoucherDto[]>([]);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [filteredVouchers, setFilteredVouchers] = useState<VoucherDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [editVoucher, setEditVoucher] = useState<VoucherDto | null>(null);
  const [previewVoucher, setPreviewVoucher] = useState<VoucherDto | null>(null);
  const [cashParentAccountId, setCashParentAccountId] = useState<number | null>(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    draft: 0,
    posted: 0,
    totalAmount: 0
  });

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Load data on mount
  useEffect(() => {
    loadCashParentAccount();
    loadVouchers();
  }, []);

  // Apply filters and calculate stats
  useEffect(() => {
    applyFilters();
    calculateStats();
  }, [vouchers, statusFilter, searchTerm]);

  const applyFilters = () => {
    let filtered = [...vouchers];

    if (statusFilter !== 'All') {
      filtered = filtered.filter(v => v.status === statusFilter);
    }

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(v =>
        v.voucherNumber.toLowerCase().includes(search) ||
        (v.notes && v.notes.toLowerCase().includes(search))
      );
    }

    setFilteredVouchers(filtered);
  };

  const calculateStats = () => {
    const total = vouchers.length;
    const draft = vouchers.filter(v => v.status === VOUCHER_STATUS.DRAFT.code).length;
    const posted = vouchers.filter(v => v.status === VOUCHER_STATUS.POSTED.code).length;
    const totalAmount = vouchers.reduce((sum, v) => sum + v.amount, 0);

    setStats({ total, draft, posted, totalAmount });
  };

  // Load Cash Parent Account ID
  const loadCashParentAccount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${config.API_BASE_URL}/SystemSettings/CashParentAccountId`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.value) {
        setCashParentAccountId(parseInt(response.data.value));
      } else {
        showSnackbar(isRTL
          ? 'لم يتم تكوين حساب الصناديق. يرجى الذهاب إلى الإعدادات → المحاسبة'
          : 'Cash parent account not configured. Please go to Settings → Accounting', 'error');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to load cash parent account');
      showSnackbar(isRTL
        ? 'لم يتم تكوين حساب الصناديق. يرجى الذهاب إلى الإعدادات → المحاسبة'
        : 'Cash parent account not configured. Please go to Settings → Accounting', 'error');
    }
  };

  // Load vouchers
  const loadVouchers = async () => {
    try {
      setLoading(true);
      const data = await cashReceiptsService.getAll();
      setVouchers([...data].sort((a, b) => b.voucherId - a.voucherId));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to load vouchers');
      showSnackbar(isRTL ? 'فشل تحميل السندات' : 'Failed to load vouchers', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Show snackbar
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Close snackbar
  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Open dialog for new voucher
  const handleAdd = () => {
    if (!cashParentAccountId) {
      showSnackbar('Cash parent account not configured', 'error');
      return;
    }
    setEditVoucher(null);
    setDialogOpen(true);
  };

  // Open dialog for edit
  const handleEdit = (voucher: VoucherDto) => {
    if (voucher.status === VOUCHER_STATUS.POSTED.code) {
      showSnackbar(isRTL ? 'لا يمكن تعديل سند مرحّل' : 'Cannot edit posted voucher', 'error');
      return;
    }
    setEditVoucher(voucher);
    setDialogOpen(true);
  };

  // ✅ إصلاح: جلب التفاصيل الكاملة قبل عرض المعاينة
  const handlePreview = async (voucher: VoucherDto) => {
    try {
      const fullVoucher = await cashReceiptsService.getById(voucher.voucherId);
      setPreviewVoucher(fullVoucher);
      setPreviewDialogOpen(true);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to load voucher details');
      showSnackbar(isRTL ? 'فشل تحميل تفاصيل السند' : 'Failed to load voucher details', 'error');
    }
  };

  // Save (Create or Update)
  const handleSave = async (dto: CreateVoucherDto | UpdateVoucherDto) => {
    try {
      if ('voucherId' in dto) {
        await cashReceiptsService.update(dto.voucherId, dto);
        showSnackbar(isRTL ? 'تم التحديث بنجاح' : 'Updated successfully', 'success');
      } else {
        await cashReceiptsService.create(dto);
        showSnackbar(isRTL ? 'تم الإنشاء بنجاح' : 'Created successfully', 'success');
      }
      
      setDialogOpen(false);
      setEditVoucher(null);
      await loadVouchers();
    } catch (err: any) {
      throw new Error(err.response?.data?.message || err.message);
    }
  };

  // Delete
  const handleDelete = async (voucher: VoucherDto) => {
    if (voucher.status === VOUCHER_STATUS.POSTED.code) {
      showSnackbar(isRTL ? 'لا يمكن حذف سند مرحّل' : 'Cannot delete posted voucher', 'error');
      return;
    }

    const ok = await confirm({ message: isRTL ? 'هل أنت متأكد من الحذف؟' : 'Are you sure you want to delete?', severity: 'error' });
    if (!ok) return;
    setActionLoading(`delete-${voucher.voucherId}`);
    try {
      await cashReceiptsService.delete(voucher.voucherId);
      showSnackbar(isRTL ? 'تم الحذف بنجاح' : 'Deleted successfully', 'success');
      await loadVouchers();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to delete');
      showSnackbar(err.response?.data?.message || (isRTL ? 'فشل الحذف' : 'Failed to delete'), 'error');
    } finally {
      setActionLoading(null);
    }
  };

  // Post voucher
  const handlePost = async (voucher: VoucherDto) => {
    if (voucher.status === VOUCHER_STATUS.POSTED.code) {
      showSnackbar(isRTL ? 'السند مرحّل بالفعل' : 'Voucher already posted', 'info');
      return;
    }

    const ok = await confirm({ message: isRTL ? 'هل تريد ترحيل السند؟' : 'Do you want to post this voucher?', severity: 'warning' });
    if (!ok) return;
    setActionLoading(`post-${voucher.voucherId}`);
    try {
      await cashReceiptsService.post(voucher.voucherId);
      showSnackbar(isRTL ? 'تم الترحيل بنجاح' : 'Posted successfully', 'success');
      await loadVouchers();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to post');
      showSnackbar(err.response?.data?.message || (isRTL ? 'فشل الترحيل' : 'Failed to post'), 'error');
    } finally {
      setActionLoading(null);
    }
  };

  // Unpost voucher
  const handleUnpost = async (voucher: VoucherDto) => {
    if (voucher.status === VOUCHER_STATUS.DRAFT.code) {
      showSnackbar(isRTL ? 'السند غير مرحّل' : 'Voucher not posted', 'info');
      return;
    }

    const ok = await confirm({ message: isRTL ? 'هل تريد إلغاء الترحيل؟' : 'Do you want to unpost this voucher?', severity: 'warning' });
    if (!ok) return;
    setActionLoading(`unpost-${voucher.voucherId}`);
    try {
      await cashReceiptsService.unpost(voucher.voucherId);
      showSnackbar(isRTL ? 'تم إلغاء الترحيل بنجاح' : 'Unposted successfully', 'success');
      await loadVouchers();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to unpost');
      showSnackbar(err.response?.data?.message || (isRTL ? 'فشل إلغاء الترحيل' : 'Failed to unpost'), 'error');
    } finally {
      setActionLoading(null);
    }
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditVoucher(null);
  };

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
          <Card 
            elevation={4} 
            sx={{ 
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', 
              color: 'white',
              transition: 'transform 0.3s',
              '&:hover': { transform: 'translateY(-5px)' }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{stats.total}</Typography>
                  <Typography variant="body2">{isRTL ? 'إجمالي السندات' : 'Total Receipts'}</Typography>
                </Box>
                <MonetizationOnIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
          <Card 
            elevation={4} 
            sx={{ 
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', 
              color: 'white',
              transition: 'transform 0.3s',
              '&:hover': { transform: 'translateY(-5px)' }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{stats.draft}</Typography>
                  <Typography variant="body2">{isRTL ? 'مسودات' : 'Draft'}</Typography>
                </Box>
                <PendingActionsIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
          <Card 
            elevation={4} 
            sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
              color: 'white',
              transition: 'transform 0.3s',
              '&:hover': { transform: 'translateY(-5px)' }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{stats.posted}</Typography>
                  <Typography variant="body2">{isRTL ? 'مرحّل' : 'Posted'}</Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
          <Card 
            elevation={4} 
            sx={{ 
              background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', 
              color: '#333',
              transition: 'transform 0.3s',
              '&:hover': { transform: 'translateY(-5px)' }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">{stats.totalAmount.toFixed(3)}</Typography>
                  <Typography variant="body2">{isRTL ? 'إجمالي المبالغ' : 'Total Amount'}</Typography>
                </Box>
                <AccountBalanceWalletIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Header */}
      <Paper elevation={3} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TrendingUpIcon sx={{ fontSize: 32, color: '#11998e' }} />
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#11998e' }}>
              {isRTL ? 'سندات القبض النقدي' : 'Cash Receipts'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title={isRTL ? 'تحديث' : 'Refresh'}>
              <IconButton 
                color="primary" 
                onClick={loadVouchers}
                sx={{
                  bgcolor: 'rgba(17, 153, 142, 0.1)',
                  '&:hover': { bgcolor: 'rgba(17, 153, 142, 0.2)' }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              disabled={!cashParentAccountId}
              sx={{
                background: 'linear-gradient(45deg, #11998e 30%, #38ef7d 90%)',
                boxShadow: '0 3px 5px 2px rgba(17, 153, 142, .3)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #0d7a6f 30%, #2dd165 90%)',
                }
              }}
            >
              {isRTL ? 'سند جديد' : 'New Receipt'}
            </Button>
          </Stack>
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label={isRTL ? 'بحث' : 'Search'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ width: 300 }}
            placeholder={isRTL ? 'رقم السند أو الملاحظات' : 'Voucher number or notes'}
          />
          <TextField
            select
            label={isRTL ? 'الحالة' : 'Status'}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            size="small"
            sx={{ width: 150 }}
          >
            <MenuItem value="All">{isRTL ? 'الكل' : 'All'}</MenuItem>
            <MenuItem value="Draft">{isRTL ? 'مسودة' : 'Draft'}</MenuItem>
            <MenuItem value="Posted">{isRTL ? 'مرحّل' : 'Posted'}</MenuItem>
          </TextField>
        </Box>
      </Paper>

      {/* Warning if cash parent not found */}
      {!cashParentAccountId && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          {isRTL 
            ? 'لم يتم العثور على حساب النقدية الرئيسي (01-002-000002). يرجى إنشاؤه أولاً.'
            : 'Cash parent account (01-002-000002) not found. Please create it first.'}
        </Alert>
      )}

      {/* Table */}
      <TableContainer 
        component={Paper} 
        elevation={3}
        sx={{ 
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Table>
          <TableHead>
            <TableRow 
              sx={{ 
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
              }}
            >
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                {isRTL ? 'رقم السند' : 'Voucher Number'}
              </TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                {isRTL ? 'التاريخ' : 'Date'}
              </TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">
                {isRTL ? 'المبلغ' : 'Amount'}
              </TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                {isRTL ? 'الحالة' : 'Status'}
              </TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                {isRTL ? 'ملاحظات' : 'Notes'}
              </TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">
                {isRTL ? 'الإجراءات' : 'Actions'}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress sx={{ color: '#11998e' }} />
                </TableCell>
              </TableRow>
            ) : filteredVouchers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {isRTL ? 'لا توجد سندات' : 'No receipts found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredVouchers.map((voucher) => {
                const isDraft = voucher.status === VOUCHER_STATUS.DRAFT.code;
                return (
                  <TableRow 
                    key={voucher.voucherId} 
                    hover
                    sx={{
                      '&:hover': {
                        bgcolor: 'rgba(17, 153, 142, 0.05)'
                      }
                    }}
                  >
                    <TableCell>
                      <Typography fontWeight="bold" color="#11998e">
                        {voucher.voucherNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {new Date(voucher.voucherDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="success.main">
                        {voucher.amount.toFixed(3)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={isRTL
                          ? (isDraft ? VOUCHER_STATUS.DRAFT.nameAr : VOUCHER_STATUS.POSTED.nameAr)
                          : (isDraft ? VOUCHER_STATUS.DRAFT.nameEn : VOUCHER_STATUS.POSTED.nameEn)
                        }
                        color={isDraft ? 'warning' : 'success'}
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                      {voucher.journalEntryNumber && (
                        <Chip label={`${voucher.journalEntryNumber} (#${voucher.journalEntryId})`} size="small" color="info" variant="outlined" sx={{ ml: 0.5 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {voucher.notes || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title={isRTL ? 'معاينة' : 'Preview'}>
                          <IconButton
                            size="small"
                            onClick={() => handlePreview(voucher)}
                            sx={{
                              color: '#11998e',
                              '&:hover': { bgcolor: 'rgba(17, 153, 142, 0.1)' }
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {isDraft && (
                          <>
                            <Tooltip title={isRTL ? 'تعديل' : 'Edit'}>
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(voucher)}
                                sx={{
                                  color: 'primary.main',
                                  '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.1)' }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={isRTL ? 'حذف' : 'Delete'}>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(voucher)}
                                disabled={actionLoading === `delete-${voucher.voucherId}`}
                                sx={{
                                  color: 'error.main',
                                  '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {canPost && (
                            <Tooltip title={isRTL ? 'ترحيل' : 'Post'}>
                              <IconButton
                                size="small"
                                onClick={() => handlePost(voucher)}
                                disabled={actionLoading === `post-${voucher.voucherId}`}
                                sx={{
                                  color: 'success.main',
                                  '&:hover': { bgcolor: 'rgba(46, 125, 50, 0.1)' }
                                }}
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            )}
                          </>
                        )}
                        {!isDraft && canPost && (
                          <Tooltip title={isRTL ? 'إلغاء الترحيل' : 'Unpost'}>
                            <IconButton
                              size="small"
                              onClick={() => handleUnpost(voucher)}
                              disabled={actionLoading === `unpost-${voucher.voucherId}`}
                              sx={{
                                color: 'warning.main',
                                '&:hover': { bgcolor: 'rgba(237, 108, 2, 0.1)' }
                              }}
                            >
                              <UndoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialogs */}
      {cashParentAccountId && (
        <CashReceiptDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          onSave={handleSave}
          editVoucher={editVoucher}
          cashParentAccountId={cashParentAccountId}
        />
      )}

      <VoucherPreviewDialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        voucher={previewVoucher}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={closeSnackbar} 
          severity={snackbar.severity} 
          sx={{ 
            width: '100%',
            borderRadius: 2,
            boxShadow: 3
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CashReceiptsPage;